import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryIcon from "@mui/icons-material/Category";
import HandshakeIcon from "@mui/icons-material/Handshake";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../../../conf/api";

/**
 * Step 1 — pick one or more live Admin Portal Categories/Partners, and
 * immediately choose which of their feeds to push into, all on one screen —
 * mirrors Step1BrandSelection.js's "pick brands, then feeds for each inline"
 * shape (its embedded BrandFeeds sub-component) instead of splitting group
 * and feed selection across two separate wizard steps.
 */
export default function Step1GroupAndFeedSelection({
  selectedGroups,
  onGroupToggle,
  selectedFeedIds,
  onFeedsChange,
  onFeedsInfoChange,
}) {
  const [groups, setGroups] = useState({ categories: [], partners: [] });
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const categoriesPicked = selectedGroups.filter((g) => g.group_type === "CATEGORY").length;

  const fetchGroups = () => {
    setGroupsLoading(true);
    setGroupsError(null);
    api
      .get("/mrss/admin-portal/groups/")
      .then((res) => {
        const data = res.data || {};
        setGroups({ categories: data.categories || [], partners: data.partners || [] });
      })
      .catch((err) => {
        setGroupsError(
          err.response?.data?.error || "Failed to load Categories/Partners from Admin Portal."
        );
      })
      .finally(() => setGroupsLoading(false));
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const isGroupSelected = (group) =>
    !!selectedGroups.find((g) => g.group_type === group.group_type && g.group_id === group.group_id);

  const renderGroupCard = (group) => {
    const isSelected = isGroupSelected(group);
    return (
      <Grid item xs={12} sm={6} md={4} key={`${group.group_type}-${group.group_id}`}>
        <Paper
          variant="outlined"
          onClick={() => onGroupToggle(group)}
          sx={{
            p: 1.5,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            border: "2px solid",
            borderColor: isSelected ? "success.main" : "divider",
            bgcolor: isSelected ? "rgba(16,185,129,0.06)" : "background.paper",
          }}
        >
          <Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} />
          {group.group_type === "CATEGORY" ? (
            <CategoryIcon color="primary" fontSize="small" />
          ) : (
            <HandshakeIcon color="warning" fontSize="small" />
          )}
          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
            {group.label}
          </Typography>
        </Paper>
      </Grid>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gutterBottom>
        <Typography variant="subtitle1" fontWeight={600}>
          Step 1 — Categories/Partners &amp; Feeds
        </Typography>
        <Tooltip title="Refresh from Admin Portal">
          <IconButton size="small" onClick={fetchGroups} disabled={groupsLoading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Select one or more of Admin Portal's own live Categories/Partners, then choose which feeds
        to push into for each — same as the Brand Publish wizard's "brands, then feeds" step.
        {selectedGroups.length > 0 && (
          <Chip label={`${selectedGroups.length} selected`} size="small" color="success" sx={{ ml: 1 }} />
        )}
      </Typography>

      {groupsError && <Alert severity="error" sx={{ mb: 2 }}>{groupsError}</Alert>}

      {groupsLoading ? (
        <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
      ) : (
        <>
          {/* Collapsed by default (user request 2026-10-13): a long list most
              pushes do not need. The header opens it and says what is picked. */}
          <Box
            mb={1}
            display="flex"
            alignItems="center"
            gap={1}
            role="button"
            tabIndex={0}
            aria-expanded={categoriesOpen}
            data-testid="categories-toggle"
            onClick={() => setCategoriesOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setCategoriesOpen((o) => !o);
              }
            }}
            sx={{ cursor: "pointer", userSelect: "none", width: "fit-content", borderRadius: 1, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}
          >
            <ExpandMoreIcon
              fontSize="small"
              color="primary"
              sx={{ transform: categoriesOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}
            />
            <Typography variant="caption" color="primary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Categories
            </Typography>
            <Chip label={groups.categories.length} size="small" />
            {categoriesPicked > 0 && <Chip label={`${categoriesPicked} selected`} size="small" color="success" />}
          </Box>
          <Collapse in={categoriesOpen}>
            {groups.categories.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>No active Category feeds in Admin Portal.</Alert>
            ) : (
              <Grid container spacing={1} mb={2}>
                {groups.categories.map(renderGroupCard)}
              </Grid>
            )}
          </Collapse>

          <Box mb={1} display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Partners
            </Typography>
            <Chip label={groups.partners.length} size="small" />
          </Box>
          {groups.partners.length === 0 ? (
            <Alert severity="info">No active Partners in Admin Portal.</Alert>
          ) : (
            <Grid container spacing={1} mb={2}>
              {groups.partners.map(renderGroupCard)}
            </Grid>
          )}
        </>
      )}

      {selectedGroups.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} mt={3} mb={1}>
            Feeds — select which feeds to push into for each selected group
          </Typography>
          {selectedGroups.map((group) => (
            <GroupFeeds
              key={`${group.group_type}-${group.group_id}`}
              group={group}
              selectedFeedIds={selectedFeedIds}
              onFeedsChange={onFeedsChange}
              onFeedsInfoChange={onFeedsInfoChange}
            />
          ))}
        </>
      )}
    </Box>
  );
}

/**
 * Fetches and renders the feeds for ONE selected group, and reports them
 * up (tagged with their origin group) so the wizard's flat `groupFeeds`
 * list always reflects every currently-selected group at once — mirrors
 * Step1BrandSelection.js's embedded BrandFeeds sub-component.
 */
function GroupFeeds({ group, selectedFeedIds, onFeedsChange, onFeedsInfoChange }) {
  const [feeds, setFeeds] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/mrss/admin-portal/groups/${encodeURIComponent(group.group_type)}/${group.group_id}/feeds/`)
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        const tagged = data.map((f) => ({
          ...f,
          _group_type: group.group_type,
          _group_id: group.group_id,
          _group_label: group.label,
        }));
        setFeeds(tagged);
        // Merge this group's feeds into the wizard's flat list — replace any
        // stale entries for this same group (e.g. a re-fetch) rather than
        // appending duplicates, but leave every other group's feeds alone.
        onFeedsInfoChange((prev) => [
          ...prev.filter((f) => !(f._group_type === group.group_type && f._group_id === group.group_id)),
          ...tagged,
        ]);
      })
      .catch((err) => {
        setFeeds([]);
        setError(err.response?.data?.error || `Failed to load feeds for "${group.label}".`);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.group_type, group.group_id]);

  const toggleFeed = (feedId) => {
    const next = selectedFeedIds.includes(feedId)
      ? selectedFeedIds.filter((id) => id !== feedId)
      : [...selectedFeedIds, feedId];
    onFeedsChange(next);
  };

  const selectAll = () =>
    onFeedsChange(Array.from(new Set([...selectedFeedIds, ...(feeds || []).map((f) => f.id)])));
  const deselectAll = () => {
    const idsToRemove = new Set((feeds || []).map((f) => f.id));
    onFeedsChange(selectedFeedIds.filter((id) => !idsToRemove.has(id)));
  };

  return (
    <Box sx={{ mt: 1.5, pl: 1, borderLeft: "3px solid", borderColor: "primary.light" }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        {group.group_type === "CATEGORY" ? (
          <CategoryIcon color="primary" fontSize="small" />
        ) : (
          <HandshakeIcon color="warning" fontSize="small" />
        )}
        <Typography variant="body2" fontWeight={700}>
          {group.label}
        </Typography>
        {feeds && feeds.length > 0 && (
          <>
            <Chip label={`${selectedFeedIds.filter((id) => feeds.some((f) => f.id === id)).length} / ${feeds.length}`} size="small" />
            <Button size="small" variant="outlined" onClick={selectAll}>
              Select all
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={deselectAll}>
              Deselect all
            </Button>
          </>
        )}
      </Box>

      {loading ? (
        <CircularProgress size={20} sx={{ display: "block", my: 1 }} />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
      ) : feeds && feeds.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 1 }}>No live feeds found for "{group.label}".</Alert>
      ) : (
        <FormGroup sx={{ mb: 1 }}>
          {(feeds || []).map((feed) => (
            <FormControlLabel
              key={feed.id}
              control={
                <Checkbox checked={selectedFeedIds.includes(feed.id)} onChange={() => toggleFeed(feed.id)} size="small" />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <RssFeedIcon fontSize="small" color="action" />
                  <Typography variant="body2">{feed.title || feed.name}</Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      )}
    </Box>
  );
}
